import type { RegisterImageReq } from '@/api/type-request'

// Token provider function type - returns fresh token on each call (with internal caching)
// Optional invalidate method to force token refresh on next call
export type TokenProvider = (() => Promise<string>) & {
  invalidate?: () => void
}

// Optional registration extras for a local-upload image (image_provider=0).
// Mirrors the URL-import payload fields; all optional, omitted when the user leaves them off.
export type ImageRegisterConfig = Pick<
  RegisterImageReq,
  'envs' | 'cmd' | 'disable_ssh' | 'min_resource'
>

export interface UploadTask {
  id: string
  name: string
  progress: number
  status: 'starting' | 'uploading' | 'pausing' | 'paused' | 'completed' | 'error' | 'pending'
  startTime: Date
  imageId?: number
  storeService?: string
  originalFile?: File
  originalDescription?: string
  // Registration extras (envs/cmd/disable_ssh/min_resource); preserved so retry re-registers
  // with the same config rather than dropping it.
  config?: ImageRegisterConfig
  format?: number // image format (0 = Container, 1 = VM);
  speed?: number
  lastLoaded?: number
  lastUpdateTime?: number
}

export interface UploadContext {
  imageId: number
  storeServiceUrl: string
  getUploadToken: TokenProvider
  md5: string
  fileName: string
  fileSize: number
}

export interface ImageRegistrationResult {
  imageId: number
  storeServiceUrl: string
  getUploadToken: TokenProvider
  md5: string
  chunkList: Blob[]
}

export interface UploadChunkResponse {
  info?: {
    blkWritten: Record<string, boolean>
  }
}

export interface RequestOptions {
  initImagePartUploadFunc: (params: {
    file: File
    imageName: string
    description: string
    fileMd5: string
    // Image format: 0 = Container (default), 1 = VM (RAW).
    format?: number
    // Optional registration extras (envs/cmd/disable_ssh/min_resource).
    config?: ImageRegisterConfig
  }) => Promise<{ imageId: number; storeServiceUrl: string; getUploadToken: TokenProvider }>
  uploadPartImageFunc: (options: {
    chunk: Blob
    index: number
    fileMd5: string
    fileName: string
    fileSize: number
    imageId: number
    storeServiceUrl: string
    getUploadToken: TokenProvider
    signal?: AbortSignal
  }) => Promise<UploadChunkResponse>
  finishImagePartUploadFunc: (params: { imageId: number; fileMd5: string }) => Promise<any>
}

export function generateTaskId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Re-export UploadPausedError from shared upload utils
export { UploadPausedError } from '@/utils/upload'
